"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IntroducedSignalOrigin = void 0;
exports.signalValuedColumnOf = signalValuedColumnOf;
exports.sliceNeedsRoster = sliceNeedsRoster;
exports.ruledSignalColumnOf = ruledSignalColumnOf;
exports.signalIdentityKey = signalIdentityKey;
exports.rosterByNameKey = rosterByNameKey;
exports.introducedSignalNames = introducedSignalNames;
exports.signalDecisionForName = signalDecisionForName;
exports.pinnedSignalNameKeysAfter = pinnedSignalNameKeysAfter;
exports.effectiveSignalDecisions = effectiveSignalDecisions;
exports.rosterAfterDecisions = rosterAfterDecisions;
exports.isSignalRuled = isSignalRuled;
const signal_name_canonicalization_1 = require("../../../../DataScience/signal-hygiene/signal-name-canonicalization");
const consensus_types_1 = require("../../../../Consensus/consensus.types");
const package_data_adjudication_types_1 = require("../../../../PackageDataAdjudication/package-data-adjudication.types");
const slice_key_1 = require("../../../../Consensus/slice-key");
const adjudication_step_types_1 = require("./adjudication-step.types");
const adjudication_admitted_rows_1 = require("./adjudication-admitted-rows");
const adjudication_slice_1 = require("./adjudication-slice");
const SIGNAL_VALUED_COLUMN_BY_SLICE = {
    [slice_key_1.SliceKey.PinAssignments]: 'signalName',
};
var IntroducedSignalOrigin;
(function (IntroducedSignalOrigin) {
    IntroducedSignalOrigin["RulingValue"] = "ruling_value";
    IntroducedSignalOrigin["RowKey"] = "row_key";
})(IntroducedSignalOrigin || (exports.IntroducedSignalOrigin = IntroducedSignalOrigin = {}));
function signalValuedColumnOf(sliceKey) {
    return SIGNAL_VALUED_COLUMN_BY_SLICE[sliceKey] ?? null;
}
function sliceNeedsRoster(sliceKey) {
    return signalValuedColumnOf(sliceKey) !== null || (0, adjudication_slice_1.signalKeyColumnOf)(sliceKey) !== null;
}
function ruledSignalColumnOf(input) {
    return input.roster.length === 0 ? null : signalValuedColumnOf(input.sliceKey);
}
function signalIdentityKey(name) {
    return (0, signal_name_canonicalization_1.canonicalizeSignalNameForComparison)(name);
}
function rosterByNameKey(roster) {
    const byKey = new Map();
    for (const signal of roster) {
        const key = signalIdentityKey(signal.name);
        if (!byKey.has(key)) {
            byKey.set(key, signal);
        }
    }
    return byKey;
}
function introducedSignalNames(input, output) {
    const introduced = new Map();
    for (const entry of [...valueIntroducedNames(input, output), ...rowKeyIntroducedNames(input, output)]) {
        const key = signalIdentityKey(entry.name);
        if (!introduced.has(key)) {
            introduced.set(key, entry);
        }
    }
    return [...introduced.values()];
}
function valueIntroducedNames(input, output) {
    const column = ruledSignalColumnOf(input);
    if (column === null) {
        return [];
    }
    const held = rosterByNameKey(input.roster);
    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
    const introduced = new Map();
    for (const decision of output.groundTruthDecisions) {
        if (decision.anchor.kind !== consensus_types_1.AnchorKind.Field || decision.anchor.column !== column) {
            continue;
        }
        if (!(0, adjudication_step_types_1.isGroundTruthRuled)(decision) || absentRowKeys.has(decision.anchor.rowKey)) {
            continue;
        }
        const name = asName(decision.rawValue);
        if (name === null) {
            continue;
        }
        const key = signalIdentityKey(name);
        if (held.has(key) || introduced.has(key)) {
            continue;
        }
        introduced.set(key, { name, anchor: decision.anchor, origin: IntroducedSignalOrigin.RulingValue });
    }
    return [...introduced.values()];
}
function rowKeyIntroducedNames(input, output) {
    const column = (0, adjudication_slice_1.signalKeyColumnOf)(input.sliceKey);
    if (column === null || input.roster.length === 0) {
        return [];
    }
    const slice = (0, adjudication_slice_1.adjudicationSliceOf)(input);
    const heldRowKeys = new Set(input.roster.map((signal) => slice.rowKeyOf({ [column]: signal.name })));
    return [...(0, adjudication_admitted_rows_1.admittedRowKeysOf)(input, output)].flatMap((rowKey) => {
        if (heldRowKeys.has(rowKey)) {
            return [];
        }
        const proposedName = (0, adjudication_admitted_rows_1.proposedRowContentOf)(input, rowKey)?.[column];
        return [
            {
                name: typeof proposedName === 'string' ? proposedName : rowKey,
                anchor: { kind: consensus_types_1.AnchorKind.Row, rowKey },
                origin: IntroducedSignalOrigin.RowKey,
            },
        ];
    });
}
function signalDecisionForName(output, name) {
    const key = signalIdentityKey(name);
    return output.signalDecisions.find((decision) => decision.name !== undefined && signalIdentityKey(decision.name) === key);
}
function pinnedSignalNameKeysAfter(input, output) {
    const column = ruledSignalColumnOf(input);
    if (column === null) {
        return new Set();
    }
    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
    const valueByRowKey = new Map();
    for (const row of input.rows) {
        valueByRowKey.set(row.rowKey, row.values[column] ?? null);
    }
    for (const decision of output.groundTruthDecisions) {
        if (decision.anchor.kind !== consensus_types_1.AnchorKind.Row || decision.existence !== package_data_adjudication_types_1.RowExistence.Present) {
            continue;
        }
        if (valueByRowKey.has(decision.anchor.rowKey)) {
            continue;
        }
        valueByRowKey.set(decision.anchor.rowKey, (0, adjudication_admitted_rows_1.proposedRowContentOf)(input, decision.anchor.rowKey)?.[column] ?? null);
    }
    for (const decision of output.groundTruthDecisions) {
        if (decision.anchor.kind !== consensus_types_1.AnchorKind.Field || decision.anchor.column !== column) {
            continue;
        }
        if ((0, adjudication_step_types_1.isGroundTruthRuled)(decision)) {
            valueByRowKey.set(decision.anchor.rowKey, decision.rawValue ?? null);
        }
    }
    const keys = new Set();
    for (const [rowKey, value] of valueByRowKey) {
        if (absentRowKeys.has(rowKey)) {
            continue;
        }
        const name = asName(value);
        if (name !== null) {
            keys.add(signalIdentityKey(name));
        }
    }
    return keys;
}
function effectiveSignalDecisions(input, output) {
    const introduced = new Set(introducedSignalNames(input, output).map((entry) => signalIdentityKey(entry.name)));
    const heldIds = new Set(input.roster.map((signal) => signal.signalId));
    return output.signalDecisions.filter((decision) => {
        if (decision.kind === adjudication_step_types_1.SignalDispositionKind.Delete) {
            return decision.signalId !== undefined && heldIds.has(decision.signalId);
        }
        return decision.name !== undefined && introduced.has(signalIdentityKey(decision.name));
    });
}
function rosterAfterDecisions(roster, decisions) {
    const renamedById = new Map();
    const deletedIds = new Set();
    const created = [];
    for (const decision of decisions) {
        if (!isSignalRuled(decision)) {
            continue;
        }
        if (decision.kind === adjudication_step_types_1.SignalDispositionKind.Rename && decision.signalId !== undefined) {
            renamedById.set(decision.signalId, decision.name ?? '');
            continue;
        }
        if (decision.kind === adjudication_step_types_1.SignalDispositionKind.Delete && decision.signalId !== undefined) {
            deletedIds.add(decision.signalId);
            continue;
        }
        if (decision.kind === adjudication_step_types_1.SignalDispositionKind.Create && decision.name !== undefined) {
            created.push({ signalId: '', name: decision.name });
        }
    }
    const surviving = roster.flatMap((signal) => deletedIds.has(signal.signalId) ? [] : [{ ...signal, name: renamedById.get(signal.signalId) ?? signal.name }]);
    return [...surviving, ...created];
}
function isSignalRuled(decision) {
    if (decision === undefined) {
        return false;
    }
    switch (decision.kind) {
        case adjudication_step_types_1.SignalDispositionKind.Rename:
            return isNonEmpty(decision.name) && isNonEmpty(decision.signalId) && (decision.clauses?.length ?? 0) > 0;
        case adjudication_step_types_1.SignalDispositionKind.Create:
            return isNonEmpty(decision.name);
        case adjudication_step_types_1.SignalDispositionKind.Delete:
            return isNonEmpty(decision.signalId);
    }
}
function isNonEmpty(value) {
    return value !== undefined && value !== '';
}
function asName(value) {
    return typeof value === 'string' && value !== '' ? value : null;
}
//# sourceMappingURL=adjudication-roster.js.map